/**
 * BLE Log Overlay: lays one goggles serial log (the file named by a GogglesLogCaptured
 * event's `path_to_log`) into the session timeline between GA4 events, by timestamp.
 *
 * The log is parsed with parseGogglesLog (src/utils/gogglesLog.js), which groups records by
 * boot session and gives each timed record `timestampMs`. This module only places those
 * records relative to the events and groups the ones with no usable time.
 * Plan: plans/ble-log-overlay/eng-plan.md.
 */

import { TIME_ROUNDING_NOTE, CLOCK_ACCURACY_NOTE } from './gogglesLog';

/** Filterable levels, in chip order. `U` is not one: U (0) records are untimed. */
export const LOG_LEVELS = ['E', 'W', 'I', 'D', 'V'];

const LOG_PATH_RE = /^logs\/[^/]+\/([^/]+)\/(goggles_[^/]+)$/;

/**
 * @param {string} pathToLog - e.g. "logs/<uid>/09_22_2026/goggles_1790132416_1790132518.txt"
 * @returns {{ day: string, filename: string }|null} null unless it is a goggles log path
 */
export const parseLogPath = (pathToLog) => {
  if (typeof pathToLog !== 'string') return null;
  const match = LOG_PATH_RE.exec(pathToLog.trim());
  return match ? { day: match[1], filename: match[2] } : null;
};

// Millisecond instant of a timeline event. eventTimestampMicros is exact; crash rows only
// have the ISO `timestamp`.
const eventTimeMs = (event) => {
  const micros = Number(event?.eventTimestampMicros);
  if (Number.isFinite(micros) && micros > 0) return Math.floor(micros / 1000);
  const parsed = Date.parse(event?.timestamp);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Every timed record in the parsed log, stable-sorted by timestampMs. Sessions are ordered
 * by id, which need not match clock order, so records are re-sorted across sessions.
 * @param {ReturnType<import('./gogglesLog').parseGogglesLog>} parsed
 */
export const collectTimedRecords = (parsed) => {
  if (!parsed) return [];
  return parsed.sessions
    .flatMap((session) => session.records.filter((r) => r.timestampMs !== null))
    .map((record, order) => ({ record, order }))
    .sort((a, b) => a.record.timestampMs - b.record.timestampMs || a.order - b.order)
    .map(({ record }) => record);
};

/**
 * Interleave timed log records with the displayed timeline events.
 *
 * @param {Array<Object>} events - the filtered event list, in display (ascending) order
 * @param {Array<Object>} records - collectTimedRecords output
 * @param {Set<string>} levels - visible levels; other records are dropped
 * @returns {{
 *   before: Array<Object>,
 *   items: Array<{ kind: 'event', event: Object, index: number } | { kind: 'logRun', records: Array<Object> }>,
 *   after: Array<Object>,
 * }} `index` is the event's position in `events`, unchanged, so state keyed by row index
 *   keeps working. At equal milliseconds the event comes first.
 */
export const mergeTimelineWithLog = (events, records, levels) => {
  const visible = records.filter((r) => levels.has(r.level));
  const before = [];
  const items = [];
  const after = [];
  if (!events.length) return { before: visible, items, after };

  let next = 0;
  const firstTime = eventTimeMs(events[0]);
  while (next < visible.length && firstTime !== null && visible[next].timestampMs < firstTime) {
    before.push(visible[next++]);
  }

  events.forEach((event, index) => {
    items.push({ kind: 'event', event, index });
    if (index === events.length - 1) return;
    const nextTime = eventTimeMs(events[index + 1]);
    if (nextTime === null) return;
    const run = [];
    while (next < visible.length && visible[next].timestampMs < nextTime) run.push(visible[next++]);
    if (run.length) items.push({ kind: 'logRun', records: run });
  });

  while (next < visible.length) after.push(visible[next++]);
  return { before, items, after };
};

/**
 * Records the overlay cannot place on the timeline, grouped for the unknown-time panel.
 * @returns {{
 *   untimed: Array<{ sessionId: number, records: Array<Object> }>,   // U (0) and unparseable
 *   beforeFirstMarker: Array<Object>,
 *   undatedSessions: Array<{ sessionId: number, records: Array<Object> }>,  // no anchor; uptime order
 *   count: number,
 * }}
 */
export const collectUnknownTimeRecords = (parsed) => {
  const empty = { untimed: [], beforeFirstMarker: [], undatedSessions: [], count: 0 };
  if (!parsed) return empty;

  const untimed = parsed.sessions
    .filter((s) => s.untimed.length)
    .map((s) => ({ sessionId: s.sessionId, records: s.untimed }));
  const undatedSessions = parsed.sessions
    .filter((s) => s.canonicalEpoch === null && s.records.length)
    .map((s) => ({ sessionId: s.sessionId, records: s.records }));
  const beforeFirstMarker = parsed.beforeFirstMarker;

  const count = untimed.reduce((n, g) => n + g.records.length, 0)
    + undatedSessions.reduce((n, g) => n + g.records.length, 0)
    + beforeFirstMarker.length;
  return { untimed, beforeFirstMarker, undatedSessions, count };
};

/** Count timed records per level, e.g. { E: 1, W: 9, I: 57 }. */
export const countByLevel = (records) => records.reduce((counts, r) => {
  counts[r.level] = (counts[r.level] || 0) + 1;
  return counts;
}, {});

const PRECISION_NOTE = `Goggles time = boot anchor + uptime, on the goggles\u2019 clock. ${TIME_ROUNDING_NOTE} `
  + `${CLOCK_ACCURACY_NOTE} Near a GA4 event, the order shown may be wrong.`;

/**
 * Hover text for the asterisk on a log line's time. Adds a clock warning when the boot's
 * observed anchors differ by more than whole-second rounding explains.
 * @param {{ anchorSpreadS: number }|undefined} session
 */
export const precisionCallout = (session) => {
  if (!session || session.anchorSpreadS <= 1) return PRECISION_NOTE;
  return `${PRECISION_NOTE} Clock warning: this boot's anchors differ by ${session.anchorSpreadS} s, more than `
    + 'rounding explains, so the goggles\u2019 clock changed during this boot.';
};

/**
 * Log line time with milliseconds in a zone, in the same 12-hour style as event rows:
 * "08:00:30.687 PM".
 */
export const formatLogLineTime = (ms, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).formatToParts(new Date(ms));
  const part = (type) => parts.find((p) => p.type === type)?.value ?? '';
  const millis = String(((ms % 1000) + 1000) % 1000).padStart(3, '0');
  return `${part('hour')}:${part('minute')}:${part('second')}.${millis} ${part('dayPeriod')}`;
};
